<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useAuthFlowStore } from '@Stores/authFlow.store'
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import useIsMobile from '@Use/useIsMobile';
import  watchViewportInnerHeight from '@Utils/setViewport';
import { useGlobalStore } from '@Stores/global';
import { useRoute, useRouter } from 'vue-router';
import { trackClientIdentityViewedInstructions, trackClientIdentityStarted } from '@Analytics/auth.mixpanel'
import _ from 'lodash';
import { FlowLayout } from '@Shared';
import { getNextAuthFlowApi, getAuthFlowStatusApi } from '@Api/authFlow.api'
import { AuthFlowStatus } from '@Types/AuthFlowState.interface';
import { resolveDeviceAuthEnabled } from '@Use/useDeviceAuthFlag';
import useStatusPolling, { type PollResult } from '@Use/useStatusPolling';

// import components
import UpcomingStepTile from '@Components/UpcomingStepTile.vue';
import QrOptionForIdv from '@Components/QrOptionForIdv.vue';

// Stores/base data
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const globalStore = useGlobalStore();
const authFlowStore = useAuthFlowStore();
const { advanceFlow } = useFlowNavigation();
const companyImageBaseUrl = useCompanyImageBaseUrl();

// for mixpanel use
const route = useRoute();
const router = useRouter();
const context = _.isNil(route.query.context?.toString()) ? '' :route.query.context?.toString();
const supportLinkVisited = ref(false);

// view constants
const { isMobile } = useIsMobile();
let flowCompleteDetected = false;

onMounted(async () => {
    globalStore.scrollToTop();
    watchViewportInnerHeight();

    trackClientIdentityViewedInstructions(context);
    syncPollingToLayout();
})

// Actions
const recordSupportLinkVisit = () => { supportLinkVisited.value = true }

const nextStep = () => {
    trackClientIdentityStarted(context, supportLinkVisited.value);
    advanceFlow();
}

// One poll of the desktop QR screen, waiting for the phone to finish IDV. A 404 from either
// API throws SessionExpiredError, which useStatusPolling turns into the session-expired screen.
const checkIdvStatus = async (): Promise<PollResult> => {
    const deviceAuthEnabled = await resolveDeviceAuthEnabled();
    const useLegacyAuth = !deviceAuthEnabled;

    // Use GetAuthFlowStatus only until first Complete is detected
    if (!useLegacyAuth && !flowCompleteDetected) {
        const flowState = await getAuthFlowStatusApi();
        if (flowState && flowState.status === AuthFlowStatus.PermissionDenied) {
            router.replace({ name: 'idv-ended' });
            return 'stop';
        }
        if (flowState && flowState.status === AuthFlowStatus.Complete) {
            flowCompleteDetected = true;
            authFlowStore._clearFlowSequence();
            advanceFlow();
            return 'continue';
        }

        // GetAuthFlowStatus failed (500) — fall back to legacy GetNextAuthFlow
        if (!flowState) {
            var fallbackRoutes = await getNextAuthFlowApi();
            if (fallbackRoutes != undefined && fallbackRoutes.length > 0) {
                authFlowStore._clearFlowSequence();
                authFlowStore.addToAuthFlow(fallbackRoutes);
                advanceFlow();
                return 'stop';
            }
            return 'failed';
        }

        return 'continue';
    }

    // Legacy path OR post-Complete path: only GetNextAuthFlow
    var newRoutes = await getNextAuthFlowApi();
    if (newRoutes == undefined) {
        return 'failed';
    }

    if (newRoutes.length > 0) {
        authFlowStore._clearFlowSequence();
        authFlowStore.addToAuthFlow(newRoutes);
        if (flowCompleteDetected) {
            // Post-Complete: stay on page, reset flag, poll with GetAuthFlowStatus
            flowCompleteDetected = false;
            return 'continue';
        }
        // Legacy: advance to the returned route
        advanceFlow();
        return 'stop';
    }

    authFlowStore._clearFlowSequence();
    advanceFlow();
    return 'stop';
}

// Only the desktop layout (the QR code) waits on the phone, so only it polls. The layout can
// switch when the window is resized. The poller stops itself on unmount.
const polling = useStatusPolling(checkIdvStatus);
const syncPollingToLayout = () => {
    if (isMobile.value) {
        polling.stop();
    } else {
        polling.start();
    }
};
watch(isMobile, syncPollingToLayout);

</script>

<template>
    <flow-layout
        v-if="isMobile"
        suppress-back-to-dashboard
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">

            <app-form-content-layout>
                <h1>Verify your identity</h1>
                <p>
                    Here's what you will need and what to expect when verifying your identity.
                </p>
                
                <app-form-content-layout>
                    <upcoming-step-tile 
                        title="Scan valid ID"
                        icon="cd-ic-scan">
                        You’ll need to scan a valid ID, like your drivers 
                        license or passport – so grab this before you start. 
                    </upcoming-step-tile>

                    <upcoming-step-tile 
                        title="Take a photo"
                        icon="cd-ic-photo-camera">
                        You'll need to take a photo of your face with your
                        device. Make sure you have good lighting.
                    </upcoming-step-tile>
                </app-form-content-layout>
            
            </app-form-content-layout>

            <action-bar-layout class="action-bar-layout">
                <app-button
                    expanded
                    type="primary"
                    size="large"
                    data-id="get-started-identity"
                    @onClick="nextStep">
                    Get Started
                </app-button>
            </action-bar-layout>

            <app-form-content-layout class="centered-content">
                <app-link
                    link-url="https://certifidhelpcenter.zendesk.com/hc/en-us/articles/24856061141019-Why-am-I-doing-this"
                    @onClick="recordSupportLinkVisit">
                    Why am I doing this?
                </app-link>

                <div class="centered-content__terms">
                    By clicking 'Get Started' you agree to our <br />
                    <app-link
                        link-url="https://certifid.com/terms/"
                        type="external">
                        Terms of Use
                    </app-link>
                    and
                    <app-link
                        link-url="https://certifid.com/privacy/"
                        type="external">
                        Privacy Policy
                    </app-link>
                </div>
            </app-form-content-layout>

    </flow-layout>

    <qr-option-for-idv v-else/>

</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

h3 {
    color: var(--ch-primary-dark);
    font-size: 1rem;
    font-weight: 700;
    margin-top: 0px;
    margin-bottom: 5px;
}


.centered-content {
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
}

.centered-content__terms {
    font-size: 0.875rem;
    text-align: center;
    line-height: 150%;
}

.action-bar-layout {
    margin: 1.5rem 0;
}
</style>

